import React, { useEffect, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Alert, Button } from 'reactstrap';
import Content from '../../../layout/Content';
import { CABIN_CREW_ACTIVITY_PATH } from '../../../../config/paths';
import { breadcrumbsPropTypes } from '../../../common/resource/proptypes/CommonPropTypes';
import {
  clearCabinCrewActivity,
  getMonthlyCabinCrewActivity,
} from '../../../../actions/traffic/CabinCrewActivity';
import CabinCrewActivitySearchForm from './CabinCrewActivitySearchForm';
import Table from '../../../common/Table';
import Loader from '../../../common/Loader';
import generateMonthlyActivityColumns from './columns/CabinCrewActivityColumns';
import Modal from '../../../common/modal/Modal';
import { clearHolidayDates, getHolidayDates, postCabinCrewMovementHistory } from '../../../../actions';
import { tzNormalizeDate } from '../../../../utils/date';
import CabinCrewMovementHistoryForm from '../cabin-crew-movement-history/CabinCrewMovementHistoryForm';

const CabinCrewActivities = ({
  breadcrumbs,
  monthlyCabinCrewActivities,
  loading,
  dispatchClearCabinCrewActivity,
  dispatchGetMonthlyCabinCrewActivity,
  dispatchPostCabinCrewMovementHistory,
  holidayDates,
  dispatchGetHolidayDates,
  dispatchClearHolidayDates,
}) => {
  const [columns, setColumns] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [initialValues, setInitialValues] = useState(null);
  const [lastSearchParams, setLastSearchParams] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [activeFilter, setActiveFilter] = useState(null);
  const [tableKey, setTableKey] = useState(0);
  const [activityLoading, setActivityLoading] = useState(false);

  useEffect(
    () => () => {
      dispatchClearCabinCrewActivity();
      dispatchClearHolidayDates();
    },
    [],
  );

  const handleCreateActivity = useCallback((cell, date) => {
    const startDateTime = tzNormalizeDate({ date, format: 'YYYY-MM-DDT00:00' });
    const endDateTime = tzNormalizeDate({ date, format: 'YYYY-MM-DDT23:59' });

    setInitialValues({
      cabinCrew: {
        value: cell.cabinCrewId,
        label: cell.fullName,
      },
      startDate: startDateTime,
      endDate: endDateTime,
    });

    setModalOpen(true);
  }, []);

  const handleCounterClick = useCallback((dateKey, filterType) => {
    if (!monthlyCabinCrewActivities || monthlyCabinCrewActivities.length === 0) return;

    const filtered = monthlyCabinCrewActivities.filter((cabinCrew) => {
      if (!cabinCrew.dayEventTypes) return false;

      const dayEventType = cabinCrew.dayEventTypes[dateKey] || 'EMPTY';
      const eventTypes = dayEventType.split('|');

      const hasItinerary = eventTypes.includes('ITINERARY');
      const hasReten = eventTypes.includes('RETEN');
      const hasMaintenance = eventTypes.includes('MAINTENANCE');
      const hasReserve = eventTypes.includes('RESERVE');
      const hasMovement = eventTypes.includes('MOVEMENT');
      const hasFreeArrivedLima = eventTypes.includes('FREE_ARRIVED_LIMA');

      switch (filterType) {
        case 'ON_TRIP':
          return hasItinerary;

        case 'ON_HOLD':
          return hasReten && !hasItinerary;

        case 'MAINTENANCE':
          return hasMaintenance && !hasItinerary && !hasReten;

        case 'RESERVE':
          return hasReserve && !hasItinerary && !hasReten && !hasMaintenance;

        case 'UNAVAILABLE':
          return hasMovement && !hasItinerary && !hasReten && !hasMaintenance;

        case 'FREE':
          return hasFreeArrivedLima && !hasItinerary && !hasReten && !hasMaintenance && !hasMovement;

        case 'NO_ACTIVITY':
          return !hasItinerary && !hasReten && !hasMaintenance && !hasMovement && !hasFreeArrivedLima;

        default:
          return false;
      }
    });

    setFilteredData(filtered);
    setActiveFilter({ dateKey, filterType });
    setTableKey((prev) => prev + 1);
  }, [monthlyCabinCrewActivities]);

  const clearFilter = useCallback(() => {
    setFilteredData([]);
    setActiveFilter(null);
    setTableKey((prev) => prev + 1);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setInitialValues(null);
  }, []);

  const refreshTable = useCallback(() => {
    if (lastSearchParams) {
      dispatchGetMonthlyCabinCrewActivity(lastSearchParams);
    }
  }, [lastSearchParams, dispatchGetMonthlyCabinCrewActivity]);

    const holidaySet = useMemo(
    () => new Set(holidayDates ? holidayDates.toJS() : []),
    [holidayDates],
  );

  useEffect(() => {
    if (monthlyCabinCrewActivities && monthlyCabinCrewActivities.length > 0) {
      const dynamicColumns = generateMonthlyActivityColumns(
        monthlyCabinCrewActivities,
        handleCreateActivity,
        handleCounterClick,
        activeFilter,
        holidaySet,
      );
      setColumns(dynamicColumns);
      setShowTable(true);
    } else {
      setShowTable(false);
      setFilteredData([]);
      setActiveFilter(null);
    }
  }, [monthlyCabinCrewActivities, activeFilter, handleCreateActivity, handleCounterClick, holidaySet]);

  const onSubmit = useCallback((formValues) => {
    const newFormValues = {
      fromDateString: tzNormalizeDate({ date: formValues.fromDateString, format: 'YYYY-MM-DD' }),
      toDateString: tzNormalizeDate({ date: formValues.toDateString, format: 'YYYY-MM-DD' }),
    }

    if (formValues.homeBaseLocation && formValues.homeBaseLocation.length > 0) {
      newFormValues.homeBaseLocationIds = formValues.homeBaseLocation.map(
        (location) => location.value,
      );
    }

    if (formValues.cabinCrewStatusType && formValues.cabinCrewStatusType.length > 0) {
      newFormValues.cabinCrewStatusTypes = formValues.cabinCrewStatusType.map(
        (status) => status.value,
      );
    }

    setLastSearchParams(newFormValues);
    dispatchGetMonthlyCabinCrewActivity(newFormValues);
    dispatchGetHolidayDates({
      fromDateString: newFormValues.fromDateString,
      toDateString: newFormValues.toDateString,
    });
    setTableKey((prev) => prev + 1);
  }, [dispatchGetMonthlyCabinCrewActivity]);

  const onSubmitMovementHistoryForm = async (formValues) => {
    const newFormValues = {
      ...formValues,
      startDate: tzNormalizeDate({ date: formValues.startDate }),
      endDate: tzNormalizeDate({ date: formValues.endDate }),
      redirect: false,
      chargeMonth: formValues.month ? formValues.month.value : null,
      chargeYear: formValues.year || null
    };

    setActivityLoading(true);
    await dispatchPostCabinCrewMovementHistory(newFormValues);

    setActivityLoading(false);

    closeModal();
    refreshTable();
  }

  const getFilterLabel = useCallback(() => {
    if (!activeFilter) return '';

    const [year, month, day] = activeFilter.dateKey.split('-');
    const date = `${day}/${month}/${year}`;

    const filterLabels = {
      ON_TRIP: 'En Viaje',
      ON_HOLD: 'En Retén',
      MAINTENANCE: 'Mantenimiento',
      RESERVE: 'Reserva',
      UNAVAILABLE: 'No Disponibles',
      FREE: 'Provincia - Lima',
      NO_ACTIVITY: 'Disponibles',
    };

    return `${filterLabels[activeFilter.filterType]} - ${date}`;
  }, [activeFilter]);

  const tableData = useMemo(
    () => (activeFilter ? filteredData : monthlyCabinCrewActivities || []),
    [activeFilter, filteredData, monthlyCabinCrewActivities],
  );

  const defaultFilterMethod = useCallback((filter, row) => {
    const id = filter.pivotId || filter.id;
    const value = row[id];

    if (value == null) return false;

    return String(value)
      .toLowerCase()
      .includes(String(filter.value).toLowerCase());
  }, []);

  const content = (
    <>
      <Modal
        id="cabin-crew-movement-history-modal"
        title="Crear Actividad de Servicio a Bordo"
        body={
          <CabinCrewMovementHistoryForm
            onSubmit={onSubmitMovementHistoryForm}
            initialValues={initialValues}
            isEdit={false}
            disableSaveButton={activityLoading}
          />
        }
        show={modalOpen}
        onClickCancel={closeModal}
        onClickClose={closeModal}
      />

      <CabinCrewActivitySearchForm onSubmit={onSubmit} initialValues={{
        fromDateString: tzNormalizeDate(),
        toDateString: tzNormalizeDate(),
      }} />

      {activeFilter && (
        <Alert color="info" className="mt-3 d-flex justify-content-between align-items-center">
          <span>
            <i className="fa fa-filter mr-2" />
            Mostrando: <strong>{getFilterLabel()}</strong> ({filteredData.length}{' '}
            tripulantes)
          </span>
          <Button color="secondary" size="sm" onClick={clearFilter}>
            <i className="fa fa-times mr-1" />
            Limpiar filtro
          </Button>
        </Alert>
      )}

      {showTable && (
        <Table
          key={tableKey}
          columns={columns}
          data={tableData}
          loading={loading}
          manual={false}
          filterable
          defaultFilterMethod={defaultFilterMethod}
        />
      )}

      {!showTable && !loading && (
        <div className="alert alert-info mt-3">
          <i className="fa fa-info-circle" /> Seleccione un mes y año para ver el
          reporte
        </div>
      )}

      {loading && !showTable && <Loader />}
    </>
  );

  return (
    <Content
      title="Tareo de Servicio a Bordo"
      breadcrumbs={breadcrumbs}
      content={content}
    />
  );
};

CabinCrewActivities.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  monthlyCabinCrewActivities: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    cabinCrewId: PropTypes.number,
    dayEventTypes: PropTypes.objectOf(PropTypes.string),
  })),
  loading: PropTypes.bool.isRequired,
  dispatchClearCabinCrewActivity: PropTypes.func.isRequired,
  dispatchGetMonthlyCabinCrewActivity: PropTypes.func.isRequired,
  dispatchPostCabinCrewMovementHistory: PropTypes.func.isRequired,
  holidayDates: PropTypes.instanceOf(),
  dispatchGetHolidayDates: PropTypes.func.isRequired,
  dispatchClearHolidayDates: PropTypes.func.isRequired,
};

CabinCrewActivities.defaultProps = {
  monthlyCabinCrewActivities: [],
  holidayDates: null
};

const mapStateToProps = ({ TrafficUnit, HumanResourcesUnit }) => ({
  breadcrumbs: [
    ...TrafficUnit.CabinCrewUnitHome.get('breadcrumbs'),
    {
      text: 'Tareo de Servicio a Bordo',
      href: CABIN_CREW_ACTIVITY_PATH,
    },
  ],
  monthlyCabinCrewActivities: TrafficUnit.CabinCrewActivity.getIn([
    'all',
    'content',
    'content',
  ]),
  loading: TrafficUnit.CabinCrewActivity.getIn(['all', 'loading']),
  holidayDates: HumanResourcesUnit.Holiday.getIn(['dates', 'content'])
});

const mapDispatchToProps = {
  dispatchClearCabinCrewActivity: clearCabinCrewActivity,
  dispatchGetMonthlyCabinCrewActivity: getMonthlyCabinCrewActivity,
  dispatchPostCabinCrewMovementHistory: postCabinCrewMovementHistory,
  dispatchGetHolidayDates: getHolidayDates,
  dispatchClearHolidayDates: clearHolidayDates,
};

export default connect(mapStateToProps, mapDispatchToProps)(CabinCrewActivities);